import React, { useCallback, useEffect, useRef, useState } from 'react';
import './CameraRafale.css';

// Caméra intégrée pour la rafale : l'aperçu reste ouvert entre deux photos —
// la seule façon d'enchaîner les prises sur iOS, où rouvrir l'appareil natif
// sans geste utilisateur est interdit. Compression « au dessin » (≤ 1600 px,
// JPEG 0,85), même patron que la PWA parking. Nécessite HTTPS (ou localhost).
export function cameraDisponible() {
  return !!(window.isSecureContext && navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
}

function CameraRafale({ onCapture, onClose, enEnvoi }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [prises, setPrises] = useState(0);
  const [miniatures, setMiniatures] = useState([]); // object URLs, dernière en tête
  const [flash, setFlash] = useState(false);
  const [erreur, setErreur] = useState('');
  const urlsRef = useRef([]);

  useEffect(() => {
    let actif = true;
    navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
      audio: false,
    }).then((stream) => {
      if (!actif) { stream.getTracks().forEach((t) => t.stop()); return; }
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
    }).catch((e) => {
      setErreur(e.name === 'NotAllowedError'
        ? "Accès à la caméra refusé — autorisez-le dans les réglages du navigateur."
        : "Caméra indisponible sur cet appareil.");
    });
    return () => {
      actif = false;
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      urlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    };
  }, []);

  const declencher = useCallback(() => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const ratio = Math.min(1, 1600 / Math.max(video.videoWidth, video.videoHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(video.videoWidth * ratio);
    canvas.height = Math.round(video.videoHeight * ratio);
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const apercu = URL.createObjectURL(blob);
      urlsRef.current.push(apercu);
      setMiniatures((m) => [apercu, ...m]);
      setPrises((n) => n + 1);
      setFlash(true);
      setTimeout(() => setFlash(false), 140);
      if (navigator.vibrate) navigator.vibrate(30);
      onCapture(new File([blob], `rafale_${Date.now()}.jpg`, { type: 'image/jpeg' }));
    }, 'image/jpeg', 0.85);
  }, [onCapture]);

  return (
    <div className="cr-plein" role="dialog" aria-modal="true" aria-label="Capture en rafale">
      {erreur ? (
        <div className="cr-erreur">
          <p>{erreur}</p>
          <button type="button" className="nf-btn" onClick={onClose}>Fermer</button>
        </div>
      ) : (
        <>
          <video ref={videoRef} autoPlay playsInline muted className="cr-video" />
          {flash && <div className="cr-flash" />}
          <div className="cr-haut">
            <span className="cr-compteur">
              {prises} photo{prises > 1 ? 's' : ''}
              {enEnvoi > 0 ? ` · ${enEnvoi} en envoi` : prises > 0 ? ' · envoyées' : ''}
            </span>
          </div>
          {miniatures.length > 0 && (
            <div className="cr-miniatures" aria-label="Photos prises">
              {miniatures.map((u, i) => (
                <img key={u} src={u} alt="" className={i === 0 ? 'cr-mini cr-mini--neuve' : 'cr-mini'} />
              ))}
            </div>
          )}
          <div className="cr-bas">
            <button type="button" className="cr-terminer" onClick={onClose}>Terminer</button>
            <button type="button" className="cr-declencheur" onClick={declencher} aria-label="Prendre une photo" />
            <span className="cr-espace" />
          </div>
        </>
      )}
    </div>
  );
}

export default CameraRafale;
